import { ref } from "vue";
import * as signalR from "@microsoft/signalr";
import { useAuthStore } from "@/stores/auth";

type Callback = (payload: any) => void;

let connection: signalR.HubConnection | null = null;
let hubUrl: string | null = null;
let stoppedOnPurpose = false;
let restartTimer: ReturnType<typeof setTimeout> | null = null;
let restartAttempt = 0;
let browserListenersAdded = false;
const isConnected = ref(false);

// Several stores can listen to the same event, so every event keeps a set of callbacks.
const listeners = new Map<string, Set<Callback>>();
// Called after a reconnect, so stores can re-fetch what they missed while offline.
const resyncCallbacks = new Set<() => void>();

// Backoff for restarts after the automatic reconnect gave up: 5s, 10s, 30s, then every 60s.
const RESTART_DELAYS_MS = [5_000, 10_000, 30_000, 60_000];

function dispatch(eventName: string, payload: unknown) {
  if (import.meta.env.DEV) {
    console.debug(`Received SignalR event [${eventName}]:`, payload);
  }
  listeners.get(eventName)?.forEach((callback) => {
    try {
      callback(payload);
    } catch (err) {
      console.error(`SignalR listener for '${eventName}' failed.`, err);
    }
  });
}

/** Registers one dispatcher per event on the current connection. */
function registerDispatcher(eventName: string) {
  if (!connection) {
    return;
  }
  connection.off(eventName);
  connection.on(eventName, (payload: unknown) => dispatch(eventName, payload));
}

function runResync() {
  resyncCallbacks.forEach((callback) => {
    try {
      callback();
    } catch (err) {
      console.error("SignalR resync callback failed.", err);
    }
  });
}

function scheduleRestart() {
  if (stoppedOnPurpose || restartTimer || !hubUrl) {
    return;
  }
  const delay =
    RESTART_DELAYS_MS[Math.min(restartAttempt, RESTART_DELAYS_MS.length - 1)];
  restartAttempt++;
  restartTimer = setTimeout(() => {
    restartTimer = null;
    void restartNow();
  }, delay);
}

async function restartNow() {
  if (stoppedOnPurpose || !hubUrl || isConnected.value) {
    return;
  }
  if (restartTimer) {
    clearTimeout(restartTimer);
    restartTimer = null;
  }
  const started = await startConnection(hubUrl);
  if (started) {
    // Events sent while we were offline are lost, so reload the data.
    runResync();
  } else {
    scheduleRestart();
  }
}

function addBrowserListeners() {
  if (browserListenersAdded || typeof window === "undefined") {
    return;
  }
  browserListenersAdded = true;
  // Phones and PWAs drop the connection while asleep; reconnect as soon as we're back.
  window.addEventListener("online", () => void restartNow());
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      void restartNow();
    }
  });
}

async function startConnection(url: string): Promise<boolean> {
  if (
    connection &&
    (connection.state === signalR.HubConnectionState.Connected ||
      connection.state === signalR.HubConnectionState.Connecting ||
      connection.state === signalR.HubConnectionState.Reconnecting)
  ) {
    isConnected.value =
      connection.state === signalR.HubConnectionState.Connected;
    return isConnected.value;
  }

  try {
    connection = new signalR.HubConnectionBuilder()
      .withUrl(url, {
        // Called before every (re)connect, so a refreshed token is used.
        accessTokenFactory: async () => {
          const currentToken = await useAuthStore().getToken();
          if (!currentToken) {
            console.error(
              "SignalR accessTokenFactory: Token missing on (re)connection attempt.",
            );
            return "";
          }
          return currentToken;
        },
      })
      .withAutomaticReconnect()
      .configureLogging(
        import.meta.env.DEV ? signalR.LogLevel.Information : signalR.LogLevel.Warning,
      )
      .build();

    connection.onclose((error) => {
      console.warn("SignalR Connection closed.", error);
      isConnected.value = false;
      // The automatic reconnect gave up (or the connection never came back); keep trying.
      scheduleRestart();
    });

    connection.onreconnecting((error) => {
      console.warn("SignalR attempting to reconnect...", error);
      isConnected.value = false;
    });

    connection.onreconnected(() => {
      isConnected.value = true;
      restartAttempt = 0;
      runResync();
    });

    listeners.forEach((_, eventName) => registerDispatcher(eventName));
    await connection.start();
    isConnected.value = true;
    restartAttempt = 0;
    return true;
  } catch (err) {
    console.error("SignalR: Connection failed to start.", err);
    isConnected.value = false;
    if (err instanceof signalR.HttpError && err.statusCode === 401) {
      console.error(
        "SignalR: Connection failed (401 Unauthorized). Token may be expired or invalid.",
      );
    }
    connection = null;
    return false;
  }
}

export function useSignalR() {
  const stopSignalR = async () => {
    stoppedOnPurpose = true;
    if (restartTimer) {
      clearTimeout(restartTimer);
      restartTimer = null;
    }
    if (connection) {
      await connection.stop();
      isConnected.value = false;
      connection = null;
    }
  };

  /** Adds a listener. Returns a function that removes exactly this listener. */
  const subscribe = <T>(eventName: string, callback: (payload: T) => void) => {
    let callbacks = listeners.get(eventName);
    if (!callbacks) {
      callbacks = new Set();
      listeners.set(eventName, callbacks);
      registerDispatcher(eventName);
    }
    callbacks.add(callback as Callback);
    return () => unsubscribe(eventName, callback);
  };

  /** Removes one listener, or all listeners of the event when no callback is given. */
  const unsubscribe = <T>(eventName: string, callback?: (payload: T) => void) => {
    const callbacks = listeners.get(eventName);
    if (!callbacks) {
      return;
    }
    if (callback) {
      callbacks.delete(callback as Callback);
    } else {
      callbacks.clear();
    }
    if (callbacks.size === 0) {
      listeners.delete(eventName);
      connection?.off(eventName);
    }
  };

  /** Runs the callback after the connection came back, e.g. to re-fetch store data. */
  const onReconnected = (callback: () => void) => {
    resyncCallbacks.add(callback);
    return () => resyncCallbacks.delete(callback);
  };

  const initSignalR = async (url: string) => {
    hubUrl = url;
    stoppedOnPurpose = false;
    addBrowserListeners();
    const started = await startConnection(url);
    if (!started) {
      scheduleRestart();
    }
  };

  return {
    isConnected,
    initSignalR,
    stopSignalR,
    subscribe,
    unsubscribe,
    onReconnected,
  };
}
